import React, { useState, useEffect } from "react";
import { X, Save, Users, Loader2 } from "lucide-react";
import baseapi from "../../../services/baseapi";
import integrationapi from "../../../services/integrationapi";
import { showError, showSuccess } from "../../../utils/toast";
import MultiSelectApiDropdownWithAll from "../../../components/MultiSelectApiDropdownWithAll";

interface ManageClientsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    integrationData: any;
}

const getProviderSlug = (integrationType: string) => {
    switch (integrationType) {
        case 'microsoft_teams':
        case 'teams':
            return 'teams';
        case 'microsoft_sso':
        case 'microsoft-sso':
        case 'sso':
            return 'microsoft-sso';
        case 'google_sso':
        case 'google-sso':
            return 'google-sso';
        case 'google_workspace':
        case 'google-workspace':
        case 'google':
            return 'google-workspace';
        case 'external_api':
        case 'external_api_key':
            return 'external-api';
        case 'smtp':
            return 'smtp';
        case 'webhook':
            return 'webhook';
        case 'rundesk':
            return 'rundesk';
        case 'slack':
            return 'slack';
        default:
            return integrationType;
    }
};

const ManageClientsModal: React.FC<ManageClientsModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    integrationData
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [clients, setClients] = useState<any[]>([]);
    const [selectedClients, setSelectedClients] = useState<any[]>([]);

    useEffect(() => {
        const fetchClients = async () => {
            if (!isOpen) return;
            setIsLoading(true);
            try {
                const response = await baseapi.get("/clients", { params: { limit: 1000 } });
                if (response.data.status) {
                    setClients(response.data.data || []);
                }
            } catch (error) {
                console.error("Failed to fetch clients:", error);
                showError("Failed to fetch clients list");
            } finally {
                setIsLoading(false);
            }
        };

        fetchClients();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || isLoading) return;

        const allowedIds = integrationData?.allowed_client_ids || [];
        if (allowedIds.includes("*")) {
            setSelectedClients([{ id: "*", name: "Select all" }]);
        } else {
            const matched = clients.filter(c => 
                allowedIds.includes(c.id?.toString()) || 
                allowedIds.includes(Number(c.id))
            );
            // Fallback for any client ID that was not found in the fetched list
            const fallback: any[] = [];
            allowedIds.forEach((id: any) => {
                if (id !== "*" && !matched.some(c => c.id?.toString() === id.toString())) {
                    fallback.push({ id: id, name: `Client ID: ${id}` });
                }
            });
            setSelectedClients([...matched, ...fallback]);
        }
    }, [isOpen, isLoading, clients, integrationData]);

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!integrationData) return;

        setIsSaving(true);
        try {
            const hasSelectAll = selectedClients.some(c => c.id === "*");
            const allowedIds = hasSelectAll 
                ? ["*"] 
                : selectedClients.map(c => c.id.toString());

            const provider = getProviderSlug(integrationData.integration_type);
            const response = await integrationapi.patch(`integrations/${provider}/allowed-clients`, {
                allowed_client_ids: allowedIds
            });

            if (response.data.status) {
                showSuccess("Allowed clients updated successfully");
                onSuccess();
                onClose();
            } else {
                showError("Failed to update allowed clients");
            }
        } catch (error: any) {
            console.error("Failed to update allowed clients:", error);
            showError(error.response?.data?.message || error.message || "An error occurred");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 dark:bg-black/80 backdrop-blur-md animate-in fade-in duration-300"
            onClick={handleBackdropClick}
        >
            <div className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-700/50 rounded-2xl w-full max-w-xl shadow-2xl animate-in zoom-in duration-300 relative flex flex-col max-h-[90vh]">
                <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between shrink-0 bg-gray-50/50 dark:bg-[#111827]">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                            <Users className="text-orange-500 w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Manage Clients</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Control client access for {integrationData?.name}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-white p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors cursor-pointer">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                    <div className="p-8 space-y-6 overflow-y-auto">
                        {isLoading ? (
                            <div className="py-12 text-center">
                                <Loader2 className="animate-spin text-orange-500 w-8 h-8 mx-auto mb-3" />
                                <p className="text-sm text-gray-500 dark:text-gray-400">Loading clients data...</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 text-xs text-orange-950 dark:text-orange-200/70 leading-relaxed">
                                    Configure which clients are allowed to use this integration. Selecting "Select all" will grant access to all current and future clients.
                                </div>

                                <MultiSelectApiDropdownWithAll
                                    label="Allowed Clients"
                                    icon={Users}
                                    apiEndpoint="/clients"
                                    displayProperty="name"
                                    idProperty="id"
                                    searchFields={['name', 'display_name']}
                                    value={selectedClients}
                                    onChange={(items) => setSelectedClients(items)}
                                    placeholder="Select allowed clients..."
                                    searchInputPlaceholder="Type to search clients..."
                                />
                            </div>
                        )}
                    </div>

                    <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex justify-end items-center gap-4 shrink-0 bg-gray-50/50 dark:bg-[#111827]">
                        <button type="button" onClick={onClose} className="px-6 py-3 border border-gray-300 dark:border-gray-700 rounded-xl text-sm font-bold text-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all cursor-pointer">
                            Cancel
                        </button>

                        <button type="submit" disabled={isLoading || isSaving} className="px-6 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-xl text-sm font-bold shadow-lg shadow-orange-900/20 disabled:opacity-50 transition-all flex justify-center items-center gap-2 cursor-pointer">
                            {isSaving ? <Loader2 className="animate-spin w-4 h-4" /> : <Save className="w-4 h-4" />}
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ManageClientsModal;
